// Automatic Check — Amine (2026-10-08).
//
// Pure validation engine for a stored Interpretation service row. No React,
// no side effects, never throws: every rule is wrapped in try/catch, and a
// rule whose needed fields are absent from the payload (undefined) skips
// silently. DECIMAL strings ("0.00") are coerced with Number().
//
// Usage (2026-10-12 — category-aware):
//   const issues = runServiceChecks(service, "Interpretation", {
//     mileageRates,   // rows from GET /mileageRates ({ year, rate, months })
//     linguistsById,  // { [id]: linguist } lookup for service.Linguist.id
//     checkConfig,    // optional GET /validationChecks rows (Settings overrides)
//   });
//   // category: "Interpretation" | "Translation" | "OtherService" — the
//   // services row literal "Other" and legacy "All" scopes are tolerated.
//   // Back-compat: runInterpretationChecks(service, context) ===
//   // runServiceChecks(service, "Interpretation", context) (identical
//   // results for the Interpretation screens).
//   const worst = getWorstCheckSeverity(issues); // "error" | "warning" | null
//
// Check scopes (2026-10-12): every validationchecks row carries a CSV
// ServiceCategory in canonical order — "Interpretation,Translation,..."
// with tokens exactly Interpretation / Translation / OtherService. The
// legacy single value "All" reads as all three on read; matching is
// case-insensitive and Other↔OtherService tolerant (getCheckCategories /
// checkAppliesToCategory). A blank scope matches no category (the pre-CSV
// strict-match behavior).
//
// CONVERTED built-ins (2026-10-12): the codes below are NO LONGER
// evaluated by code rule fns in this file — their behavior ships as seeded
// DB Conditions evaluated with the custom-check machinery (Applies gate +
// Fails fire), scoped via the row's CSV ServiceCategory:
//   simultaneous_rate_missing, simultaneous_client_rate_missing,
//   charge_zero_completed, completed_missing_actuals, actual_time_order,
//   paid_no_charge, cancelled_nonzero_totals, time_order
//
// CODE-BACKED rules: when their DB row also carries Settings-added
// Applies-clause Conditions, those conditions act as an ADDITIONAL GATE
// (the code fn's result is kept only when the gate passes; Fails clauses
// on code-backed rows are ignored — the Settings UI hides them).
//
// Issue: { code: string, severity: "error" | "warning", message: string }
//
// Code-backed rules (code — severity — condition):
//   missing_linguist                 error   Confirmed/Completed without a linguist
//   tlp_missing                      error   AUTO mode, base pay > 0, Total Linguist Pay blank/$0
//   tlp_missing_manual               warning Manual mode: the total is calculated live, not stored
//   total_cost_missing               error   AUTO mode, base pay > 0, Total Cost blank/$0
//   total_cost_missing_manual        warning Manual mode: the total is calculated live, not stored
//     (all four auto-totals codes skip the zero-expected cancel statuses —
//     Cancelled (With 24 Hrs Notice) / Cancelled (Unable To Fill); see
//     isZeroExpectedCancelStatus, 2026-10-14 directive)
//   mileage_miles_without_cost       error   Roundtrip miles > 0 but no mileage cost
//   mileage_rate_mismatch            error   Mileage cost ≠ miles × date-resolved rate (±$0.01)
//   mileage_cost_without_miles       error   Mileage cost > 0 but roundtrip miles blank/0
//   mileage_flag_excluded            warning A normally-linguist-paid line excluded from Total Linguist Pay
//   line_excluded_all_totals         error   A cost line counts toward no total at all
//   duration_mismatch                warning Stored Duration ≠ 15-min-ceil of end − start
//   profit_margin_mismatch           warning Stored Profit/Margin disagree with charge − cost
//   cancelled_flag_mismatch          warning Cancelled flag and status disagree
//   contacts_incomplete              warning A contact row has a name or a phone, not both
//   email_thread_invalid             error   Stored email thread reference has no usable thread id
//   in_person_no_mileage             error   In-person Confirmed/Completed without mileage recorded
//   virtual_phone_mileage            warning Virtual/Phone/OPI Confirmed/Completed with mileage recorded
//   simultaneous_rate_same_as_standard warning Simultaneous linguist rate equals the standard Rate
//   prep_sched_exceed_duration       warning Preparation + Scheduling leave no core time within Duration
//   2026-10-30 — date integrity (all low-noise, measured before shipping):
//   deadline_passed                 warning Open Translation/Other past its Deadline
//   deadline_missing                warning Open Translation/Other with no Deadline at all
//   deadline_before_date            error   Deadline earlier than the assignment Date
//   delivery_before_date            error   DeliveryDate earlier than the assignment Date
//   interpretation_unclosed         warning Interpretation dated in the past, still Requested/Confirmed
//   paid_unfinished                 warning LinguistPaid while the work is not delivered/completed
//   2026-11-03 — cost completeness (Interpretation only, all warnings):
//   interpretation_linguist_pay_missing warning Completed + linguist, no payable line, blank totals
//   interpretation_total_cost_missing   warning Included cost lines carry money, Total Cost blank/$0
//   total_cost_without_breakdown        warning Total Cost > 0 but no cost line carries an amount
//
// Settings-defined CUSTOM checks (CONDITIONS builder, 2026-10-09):
//   Rows from GET /validationChecks whose IsBuiltIn is falsy (and whose
//   ServiceCategory matches the service being evaluated) are evaluated by
//   runCustomServiceChecks and merged into the SAME issue pipeline as the
//   built-in rules — identical { code, severity, message } shape, with
//   `code` = the row's DB Code. Disabled custom rows are dropped by the
//   config overlay exactly like built-ins. A custom check FAILS a row when
//   its Applies clause passes (no Applies conditions → applies to every
//   row) AND its Fails clause passes. severity = the row's Severity
//   (fallback "warning"); message = Description, else Name.
//
//   Condition semantics (Clause 'Applies'|'Fails' + per-row JoinOperator):
//     * Numeric ops (greaterThan, greaterThanOrEqual, lessThan,
//       lessThanOrEqual, between, greaterThanField, lessThanField): both
//       sides coerced with Number(); blank/''/null/undefined or non-numeric
//       → the condition is false. Exception: lessThanField/greaterThanField
//       compare TWO parseable time-of-day strings ("H:mm[:ss]") as minutes
//       since midnight (seeded time_order/actual_time_order checks); when
//       only one side parses as a time the numeric rule above applies.
//     * equals/notEquals: both sides parse as numbers → numeric compare
//       (Tolerance when provided, else exact 0); both sides boolean-ish
//       (true/false, 1/0, 'true'/'false') → Boolean compare; otherwise
//       trimmed case-insensitive string compare. equalsField/notEqualsField
//       mirror this against CompareColumn's value.
//     * equalsFieldTolerance: numeric compare with Tolerance ?? 0.005.
//     * between: Value is a JSON array string "[min,max]" — inclusive
//       numeric; non-numeric ends → false.
//     * contains/notContains/startsWith/endsWith: trimmed case-insensitive
//       string compare.
//     * in/notIn: Value is a JSON array string (parse failure → single-item
//       list) — trimmed case-insensitive membership.
//     * isBlank: null/undefined/'' after trim; isNotBlank inverse.
//     * isZero: Number(value || 0) === 0 (blank counts as zero); isNotZero
//       inverse.
//     * JoinOperator folds rows left-to-right within a clause (each row
//       joins the accumulated result of the previous rows); a row missing
//       one falls back to the clause logic (AppliesLogic/FailsLogic:
//       All → AND, Any → OR). Rows ordered by Seq.
//     * Unknown operators evaluate to false (the row never matches).

import api from "services/api";
import { getMileageRateForDate } from "helpers/mileageRateHelper";
import { parseEmailThreadIds } from "helpers/ServiceHelpers";

// ---------------------------------------------------------------------------
// Coercions & small utilities
// ---------------------------------------------------------------------------

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const isTrue = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const isFalse = (value) =>
  value === false || value === 0 || value === "0" || value === "false";

const hasValue = (value) =>
  value !== null && value !== undefined && value !== "";

const formatMoney = (value) => `$${toNumber(value).toFixed(2)}`;

const isZeroOrBlank = (value) => {
  if (value === null || value === undefined || value === "") return true;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed === 0;
};

// Numeric read that keeps "blank" distinguishable from 0 — custom-check
// conditions must treat blank/null as "cannot compare" (condition false),
// unlike toNumber() which coerces blanks to 0 for the built-in rules.
const toNullableNumber = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// true/false/1/0/'true'/'false' → boolean; anything else → null (cannot
// decide a boolean compare).
const asBooleanish = (value) => {
  if (value === true || value === 1 || value === "1" || value === "true") {
    return true;
  }
  if (value === false || value === 0 || value === "0" || value === "false") {
    return false;
  }
  return null;
};

// Trimmed, lowercased string for custom-check string compares.
const normalizeString = (value) =>
  String(value === null || value === undefined ? "" : value)
    .trim()
    .toLowerCase();

// null / undefined / '' after trim — the custom-check isBlank definition.
const isBlankValue = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "");

// "HH:mm" or "HH:mm:ss" (local time string as stored on the assignment).
const parseTimeToMinutes = (value) => {
  if (typeof value !== "string") return null;
  const match = value.trim().match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3] || 0);
  if (hours > 23 || minutes > 59 || seconds > 59) return null;
  return hours * 60 + minutes + seconds / 60;
};

// "00:00:00" is the platform's "no time" sentinel — never a real start/end.
const hasRealTime = (value) =>
  typeof value === "string" &&
  value.trim() !== "" &&
  value.trim() !== "00:00:00" &&
  parseTimeToMinutes(value) !== null;

// "HH:mm:ss" duration (may exceed 23h — dayjs UTC durations are stored raw).
const parseDurationToMinutes = (value) => {
  if (typeof value !== "string") return null;
  const match = value.trim().match(/^(\d+):(\d{2}):(\d{2})$/);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]) + Number(match[3]) / 60;
};

const formatDurationMinutes = (minutes) => {
  const total = Math.round(minutes);
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:00`;
};

// ---------------------------------------------------------------------------
// Service categories (CSV scopes, 2026-10-12) + category-aware status
// ---------------------------------------------------------------------------

// Canonical category tokens — the same three the server catalog uses
// (server/helpers/validationCheckCatalog.js, SERVICE_CATEGORIES). Scope
// strings on validationchecks rows are CSV in this order; the legacy single
// value "All" reads as all three on read, and the services table's literal
// for Other Services ("Other") is tolerated anywhere a category is read.
const SERVICE_CATEGORY_TOKENS = [
  "Interpretation",
  "Translation",
  "OtherService",
];

// "interpretation" | "translations" | "other" | "Other Service" → canonical
// token; anything unknown/blank → null.
const normalizeServiceCategory = (value) => {
  const compact = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  if (compact === "interpretation" || compact === "interpretations") {
    return "Interpretation";
  }
  if (compact === "translation" || compact === "translations") {
    return "Translation";
  }
  if (
    compact === "otherservice" ||
    compact === "otherservices" ||
    compact === "other"
  ) {
    return "OtherService";
  }
  return null;
};

// Category scope of a check row: CSV ServiceCategory → canonical token array
// (canonical order, deduped). Legacy "All" → all three. Unknown tokens and
// blank scopes → [] (matches no category — the pre-CSV strict-match result
// for rows that never named a category).
export const getCheckCategories = (row) => {
  const raw = String(row?.ServiceCategory ?? "").trim();
  if (!raw) return [];
  const found = [];
  for (const part of raw.split(",")) {
    const compact = part.trim().toLowerCase().replace(/[\s_-]+/g, "");
    if (compact === "all") return [...SERVICE_CATEGORY_TOKENS];
    const token = normalizeServiceCategory(part);
    if (token && !found.includes(token)) found.push(token);
  }
  return SERVICE_CATEGORY_TOKENS.filter((token) => found.includes(token));
};

// Scope membership: case-insensitive, Other↔OtherService tolerant. A blank
// or unknown category argument matches nothing.
export const checkAppliesToCategory = (row, category) => {
  const target = normalizeServiceCategory(category);
  if (!target) return false;
  return getCheckCategories(row).includes(target);
};

// Per-category status column on the shared services table (mirrors
// CATEGORY_STATUS_COLUMNS in server/helpers/validationCheckCatalog.js).
const STATUS_FIELD_BY_CATEGORY = {
  Interpretation: "InterpretationStatus",
  Translation: "TranslationStatus",
  OtherService: "OtherServiceStatus",
};

const getStatusFieldName = (category) =>
  STATUS_FIELD_BY_CATEGORY[normalizeServiceCategory(category)] ||
  STATUS_FIELD_BY_CATEGORY.Interpretation;

// Category-aware status read. The default "Interpretation" keeps the exact
// legacy behavior of every rule that ran before categories existed. This
// value also backs the VIRTUAL "Status" condition column in the custom-
// check evaluator below, so one seeded condition row works for every scope.
const getStatus = (service, category = "Interpretation") =>
  String(service?.[getStatusFieldName(category)] || "").trim();

// Interpretation type from either payload shape (ServiceSubtype.Name on
// embedded service payloads, InterpretationType on flat list rows).
const getInterpretationTypeName = (service) =>
  String(
    service?.ServiceSubtype?.Name || service?.InterpretationType || "",
  ).trim();

// Status gate for the type-based rules: Requested has nothing to bill yet,
// Cancelled variants are no longer billed.
const isConfirmedOrCompleted = (status) =>
  status.startsWith("Confirmed") || status.startsWith("Completed");

// Zero-expected cancel statuses (Amine directive, 2026-10-14): Cancelled
// (With 24 Hrs Notice) and Cancelled (Unable To Fill) assignments must have
// NO Account Charge / Total Cost / Total Linguist Pay — their stored totals
// are EXPECTED blank/zero. Mirrors the cancelled_nonzero_totals DB condition
// gate: trimmed, lowercased status startsWith "cancelled" AND ((contains
// "with 24" AND NOT contains "without") OR contains "unable to fill")
// → 'Cancelled (With 24 Hrs Notice)' / 'Cancelled (Unable To Fill)' gated;
// 'Cancelled (Without 24 Hrs Notice)' stays billable; 'Cancelled
// (Re-schedule)' is unchanged.
const isZeroExpectedCancelStatus = (status) => {
  const normalized = String(status || "")
    .trim()
    .toLowerCase();
  if (!normalized.startsWith("cancelled")) return false;
  return (
    (normalized.includes("with 24") && !normalized.includes("without")) ||
    normalized.includes("unable to fill")
  );
};

const isNotApplicableLinguist = (linguist) =>
  String(linguist?.Name || "")
    .trim()
    .toUpperCase() === "N/A";

// The linguist id from either the embedded object or the FK column, so list
// payloads that only carry LinguistID still resolve.
const getLinguistId = (service) => {
  const idFromObject = service?.Linguist?.id;
  if (idFromObject !== null && idFromObject !== undefined) return idFromObject;
  const idFromField = service?.LinguistID;
  return idFromField === null || idFromField === undefined ? null : idFromField;
};

const resolveLinguist = (service, context) => {
  const id = getLinguistId(service);
  if (id !== null && context?.linguistsById) {
    const fromContext = context.linguistsById[id];
    if (fromContext) return fromContext;
  }
  return service?.Linguist || null;
};

const isLinguistSelected = (service, context) => {
  const linguist = resolveLinguist(service, context);
  if (linguist && isNotApplicableLinguist(linguist)) return false;
  return getLinguistId(service) !== null;
};

// "In-Person" types bill travel; "Virtual"/"Phone"/"OPI" (over-the-phone)
// types normally don't — matched case-insensitively on the type name.
const isInPersonType = (service) => {
  const name = getInterpretationTypeName(service).toLowerCase();
  return name.includes("in-person") || name.includes("in person");
};

const isVirtualOrPhoneType = (service) => {
  const name = getInterpretationTypeName(service).toLowerCase();
  return (
    name.includes("virtual") ||
    name.includes("phone") ||
    name.includes("opi")
  );
};

// Base pay driving the totals: the stored Linguist Pay, else rate × duration.
const getBaseLinguistPay = (service) => {
  const storedPay = toNumber(service?.LinguistPay);
  if (storedPay > 0) return storedPay;
  const durationMinutes = parseDurationToMinutes(service?.Duration);
  const rate = toNumber(service?.Rate);
  if (durationMinutes === null || durationMinutes <= 0 || rate <= 0) return 0;
  return (durationMinutes / 60) * rate;
};

// ---------------------------------------------------------------------------
// Cost-line helpers
// ---------------------------------------------------------------------------

const normalizeTypeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z]/g, "");

// Which cost types normally pay the linguist (defaults map, 2026-10-08).
// Keys are normalized legacy field names AND display names.
const TYPE_PAYS_LINGUIST = {
  linguistpay: true,
  mileage: true,
  mileagecost: true,
  parkingfees: true,
  parkingfeestolls: true,
  additionallinguistcost: true,
  // 2026-11-03 (Amine): Payment Fees belong to Total Cost ONLY — a
  // payment-processing fee is the cost of getting paid, not pay owed to the
  // linguist. This map said true, which is what made the old "excluded from
  // Total Linguist Pay" warning fire on a line that is now supposed to be
  // excluded. Mirrors the cost-type + category-rule defaults.
  paymentfees: false,
  traveltime: true,
  traveltimecost: true,
  additionalcost: false,
  equipment: false,
  equipmentcost: false,
};

const getTypePaysLinguist = (line) => {
  const legacyKey = normalizeTypeKey(
    line?.SourceLegacyField || line?.CostType?.SourceLegacyField,
  );
  if (
    legacyKey &&
    Object.prototype.hasOwnProperty.call(TYPE_PAYS_LINGUIST, legacyKey)
  ) {
    return TYPE_PAYS_LINGUIST[legacyKey];
  }
  const nameKey = normalizeTypeKey(
    line?.CostType?.Name || line?.CostTypeName || line?.CustomLabel,
  );
  if (
    nameKey &&
    Object.prototype.hasOwnProperty.call(TYPE_PAYS_LINGUIST, nameKey)
  ) {
    return TYPE_PAYS_LINGUIST[nameKey];
  }
  return null;
};

const getLineLabel = (line) =>
  String(
    line?.CostType?.Name ||
      line?.CostTypeName ||
      line?.CustomLabel ||
      line?.SourceLegacyField ||
      line?.CostType?.SourceLegacyField ||
      "Cost",
  ).trim() || "Cost";

const getCostLines = (service) =>
  Array.isArray(service?.CostLines) ? service.CostLines : [];

const isMileageLine = (line) => {
  const legacyKey = normalizeTypeKey(
    line?.SourceLegacyField || line?.CostType?.SourceLegacyField,
  );
  if (legacyKey) return legacyKey === "mileage" || legacyKey === "mileagecost";
  return (
    normalizeTypeKey(
      line?.CostType?.Name || line?.CostTypeName || line?.CustomLabel,
    ) === "mileage"
  );
};

// Flag-independent mileage cost, mirroring the server's
// getServiceMileageCost(): sum of Mileage cost lines, else the compat column.
const getEffectiveMileageCost = (service) => {
  const lineSum = getCostLines(service).reduce((sum, line) => {
    if (!isMileageLine(line)) return sum;
    const amount = toNumber(line?.Amount);
    return amount > 0 ? sum + amount : sum;
  }, 0);
  if (lineSum > 0) return lineSum;
  return toNumber(service?.MileageCost);
};

// Line amount with the legacy column as fallback — stale-line amounts still
// surface the contradiction the flags describe.
const lineAmountWithLegacyFallback = (line, service) => {
  const amount = toNumber(line?.Amount);
  if (amount > 0) return amount;
  const legacyField =
    line?.SourceLegacyField || line?.CostType?.SourceLegacyField;
  if (legacyField && service?.[legacyField] != null) {
    return toNumber(service[legacyField]);
  }
  return 0;
};

const pushIssue = (issues, code, severity, message) => {
  issues.push({ code, severity, message });
};

// ---------------------------------------------------------------------------
// Rules
// ---------------------------------------------------------------------------

const checkMissingLinguist = (service, context, issues) => {
  const status = getStatus(service, context?.category);
  if (!status.startsWith("Confirmed") && !status.startsWith("Completed")) {
    return;
  }
  if (getLinguistId(service) !== null) return;
  pushIssue(
    issues,
    "missing_linguist",
    "error",
    "No linguist assigned",
  );
};

// simultaneous_rate_missing / simultaneous_client_rate_missing — CONVERTED
// to DB conditions (2026-10-12): evaluated from the seeded validationchecks
// Conditions by runServiceChecks, NOT by a code rule fn. Do not re-add one.

const checkAutoTotals = (service, context, issues) => {
  // Zero-expected cancel statuses (2026-10-14 directive): totals are
  // intentionally blank/zero — never flag them as missing.
  if (isZeroExpectedCancelStatus(getStatus(service, context?.category))) {
    return;
  }
  const basePay = getBaseLinguistPay(service);
  if (basePay <= 0) return;
  const manual = isTrue(service?.DisableAutoChargeCalc);

  if (
    service?.TotalLinguistPay !== undefined &&
    isZeroOrBlank(service?.TotalLinguistPay)
  ) {
    if (manual) {
      pushIssue(
        issues,
        "tlp_missing_manual",
        "warning",
        "Manual mode: Total Linguist Pay is calculated live, not stored",
      );
    } else {
      pushIssue(
        issues,
        "tlp_missing",
        "error",
        `Total Linguist Pay is empty (base pay ${formatMoney(basePay)})`,
      );
    }
  }

  if (service?.TotalCost !== undefined && isZeroOrBlank(service?.TotalCost)) {
    if (manual) {
      pushIssue(
        issues,
        "total_cost_missing_manual",
        "warning",
        "Manual mode: Total Cost is calculated live, not stored",
      );
    } else {
      pushIssue(
        issues,
        "total_cost_missing",
        "error",
        `Total Cost is empty (base pay ${formatMoney(basePay)})`,
      );
    }
  }
};

const checkMileage = (service, context, issues) => {
  const miles = toNumber(service?.MileageRoundtrip);
  const hasMileageCostEvidence =
    service?.MileageCost !== undefined || Array.isArray(service?.CostLines);
  if (!hasMileageCostEvidence) return;
  const cost = getEffectiveMileageCost(service);

  if (miles > 0 && cost <= 0) {
    pushIssue(
      issues,
      "mileage_miles_without_cost",
      "error",
      `Mileage cost is missing for ${String(miles)} mi`,
    );
    return;
  }

  if (
    miles > 0 &&
    cost > 0 &&
    service?.Date &&
    Array.isArray(context?.mileageRates) &&
    context.mileageRates.length > 0
  ) {
    const expectedRate = getMileageRateForDate(
      context.mileageRates,
      service.Date,
      0.7,
    );
    if (expectedRate) {
      const expectedCost = miles * expectedRate;
      if (Math.abs(cost - expectedCost) > 0.01) {
        pushIssue(
          issues,
          "mileage_rate_mismatch",
          "error",
          `Mileage cost ${formatMoney(cost)} doesn't match ${String(miles)} mi × $${Number(expectedRate).toFixed(3)}/mi (${formatMoney(expectedCost)})`,
        );
      }
    }
  }

  if (cost > 0 && miles <= 0 && service?.MileageRoundtrip !== undefined) {
    pushIssue(
      issues,
      "mileage_cost_without_miles",
      "error",
      `Mileage cost ${formatMoney(cost)} is set but no mileage recorded`,
    );
  }
};

const checkMileageFlagExcluded = (service, context, issues) => {
  for (const line of getCostLines(service)) {
    if (getTypePaysLinguist(line) !== true) continue;
    // Only an EXPLICITLY stored false flag is a contradiction — unset flags
    // resolve to the type's default (true) on hydration.
    if (!isFalse(line?.IncludeInLinguistPay)) continue;
    const inTotalCost = !isFalse(line?.IncludeInTotalCost);
    const inAccountCharge = isTrue(line?.IncludeInAccountCharge);
    // A line out of ALL totals is reported by line_excluded_all_totals — this
    // rule flags an in/out contradiction, not a silent zero.
    if (!inTotalCost && !inAccountCharge) continue;
    const amount = lineAmountWithLegacyFallback(line, service);
    if (amount <= 0) continue;
    pushIssue(
      issues,
      "mileage_flag_excluded",
      "warning",
      `${getLineLabel(line)} (${formatMoney(amount)}) is excluded from Total Linguist Pay`,
    );
  }
};

// charge_zero_completed — CONVERTED to DB conditions (2026-10-12): the
// seeded conditions ARE the check (evaluated by runServiceChecks).

const checkLineExcludedAllTotals = (service, context, issues) => {
  for (const line of getCostLines(service)) {
    const amount = toNumber(line?.Amount);
    if (amount <= 0) continue;
    if (!isFalse(line?.IncludeInTotalCost)) continue;
    if (!isFalse(line?.IncludeInLinguistPay)) continue;
    if (!isFalse(line?.IncludeInAccountCharge)) continue;
    pushIssue(
      issues,
      "line_excluded_all_totals",
      "error",
      `Cost line "${getLineLabel(line)}" (${formatMoney(amount)}) counts toward no total`,
    );
  }
};

// time_order — CONVERTED to DB conditions (2026-10-12): seeded conditions
// own this check now (TimeEnd/TimeStart operators via runServiceChecks).

const checkDurationMismatch = (service, context, issues) => {
  const start = service?.TimeStart;
  const end = service?.TimeEnd;
  if (!hasRealTime(start) || !hasRealTime(end)) return;
  const startMinutes = parseTimeToMinutes(start);
  const endMinutes = parseTimeToMinutes(end);
  if (startMinutes === null || endMinutes === null) return;
  const diff = endMinutes - startMinutes;
  if (diff <= 0) return; // reversed times are reported by time_order
  const storedMinutes = parseDurationToMinutes(service?.Duration);
  if (storedMinutes === null) return;
  const expectedMinutes = Math.ceil(diff / 15) * 15;
  if (Math.abs(storedMinutes - expectedMinutes) < 0.01) return;
  pushIssue(
    issues,
    "duration_mismatch",
    "warning",
    `Duration ${String(service?.Duration).trim()} doesn't match ${start}–${end} (expected ${formatDurationMinutes(expectedMinutes)})`,
  );
};

// completed_missing_actuals — CONVERTED to DB conditions (2026-10-12): the
// seeded conditions own this check now. Keep excluded from CHECKS.

const checkProfitMargin = (service, context, issues) => {
  if (service?.AccountCharge === undefined || service?.TotalCost === undefined) {
    return;
  }
  // A null/blank Total Cost means "not stored" (manual mode persists the
  // display-only calc as null) — profit cannot be verified against a cost
  // that was deliberately not persisted.
  if (!hasValue(service?.TotalCost)) return;
  const charge = toNumber(service?.AccountCharge);
  if (charge <= 0) return;
  const cost = toNumber(service?.TotalCost);
  const expectedProfit = charge - cost;
  const expectedMargin = (expectedProfit / charge) * 100;

  const mismatches = [];
  if (
    hasValue(service?.Profit) &&
    Math.abs(toNumber(service.Profit) - expectedProfit) > 0.01
  ) {
    mismatches.push(
      `Profit ${formatMoney(service.Profit)} vs ${formatMoney(expectedProfit)}`,
    );
  }
  if (
    hasValue(service?.Margin) &&
    Math.abs(toNumber(service.Margin) - expectedMargin) > 0.05
  ) {
    mismatches.push(
      `margin ${toNumber(service.Margin).toFixed(2)}% vs ${expectedMargin.toFixed(2)}%`,
    );
  }
  if (mismatches.length === 0) return;
  pushIssue(
    issues,
    "profit_margin_mismatch",
    "warning",
    `${mismatches.join(" and ")} — re-save to refresh`,
  );
};

const checkCancelledFlagMismatch = (service, context, issues) => {
  // Presence gate on the CATEGORY's status column: undefined = field not in
  // the payload → cannot judge, skip (legacy behavior generalized).
  if (service?.[getStatusFieldName(context?.category)] === undefined) return;
  const status = getStatus(service, context?.category);
  const flagged = isTrue(service?.Cancelled);
  const statusSaysCancelled = status.startsWith("Cancel");
  if (flagged === statusSaysCancelled) return;
  if (flagged) {
    pushIssue(
      issues,
      "cancelled_flag_mismatch",
      "warning",
      `Cancelled flag is set but status is "${status || "blank"}"`,
    );
  } else {
    pushIssue(
      issues,
      "cancelled_flag_mismatch",
      "warning",
      `Status "${status}" but Cancelled flag is off`,
    );
  }
};

// paid_no_charge — CONVERTED to DB conditions (2026-10-12): the seeded
// conditions own this check now (Paid + AccountCharge operators).

const checkContactsIncomplete = (service, context, issues) => {
  const rows = Array.isArray(service?.ContactPersons)
    ? service.ContactPersons
    : [];
  for (const row of rows) {
    const name = String(row?.Name ?? row?.name ?? "").trim();
    const phone = String(row?.PhoneNumber ?? row?.phone ?? "").trim();
    if (Boolean(name) === Boolean(phone)) continue; // both present or both absent
    if (name) {
      pushIssue(
        issues,
        "contacts_incomplete",
        "warning",
        `Contact "${name}" has no phone number`,
      );
    } else {
      pushIssue(
        issues,
        "contacts_incomplete",
        "warning",
        `Contact phone ${phone} has no name`,
      );
    }
  }
};

const checkEmailThreadInvalid = (service, context, issues) => {
  const raw = service?.EmailThreadID;
  if (raw === undefined || raw === null || String(raw).trim() === "") return;
  if (parseEmailThreadIds(raw).length > 0) return;
  pushIssue(
    issues,
    "email_thread_invalid",
    "error",
    "Email thread link has no usable thread id — re-link",
  );
};

// 2026-10-09 — In-person assignments bill mileage; a Confirmed/Completed
// in-person row with none recorded is almost always a data-entry miss.
const checkInPersonNoMileage = (service, context, issues) => {
  if (!isConfirmedOrCompleted(getStatus(service, context?.category))) return;
  if (!isInPersonType(service)) return;
  const miles = toNumber(service?.MileageRoundtrip);
  const cost = getEffectiveMileageCost(service);
  if (miles > 0 || cost > 0) return;
  pushIssue(
    issues,
    "in_person_no_mileage",
    "error",
    "In-person assignment has no mileage recorded",
  );
};

// 2026-10-09 — Virtual/Phone/OPI work is remote: mileage on these rows is
// normally not billed (warning, not error — travel CAN be agreed on).
const checkVirtualPhoneMileage = (service, context, issues) => {
  if (!isConfirmedOrCompleted(getStatus(service, context?.category))) return;
  if (!isVirtualOrPhoneType(service)) return;
  const miles = toNumber(service?.MileageRoundtrip);
  const cost = getEffectiveMileageCost(service);
  if (miles <= 0 && cost <= 0) return;
  pushIssue(
    issues,
    "virtual_phone_mileage",
    "warning",
    `Virtual/Phone assignment has ${formatMoney(cost)} of mileage — normally not billed`,
  );
};

// cancelled_nonzero_totals — CONVERTED to DB conditions (2026-10-12): the
// seeded conditions encode the zero-expected cancel sub-statuses (Unable To
// Fill / With 24 Hrs Notice) and the nonzero-total test. No code fn here.

// 2026-10-09 — A simultaneous assignment whose simultaneous rate equals the
// standard rate may be intentional (agreed) — flagged for verification.
// Tolerance defaults to ±$0.005 and can be overridden from Settings.
const checkSimultaneousRateSameAsStandard = (service, context, issues) => {
  if (String(service?.Mode || "").trim() !== "Simultaneous") return;
  if (!isLinguistSelected(service, context)) return;
  const rate = toNumber(service?.Rate);
  if (rate <= 0) return;
  if (!hasValue(service?.RateSimultaneous)) return;
  const simultaneousRate = toNumber(service?.RateSimultaneous);
  if (
    Math.abs(simultaneousRate - rate) > getSimultaneousRateTolerance(context)
  ) {
    return;
  }
  pushIssue(
    issues,
    "simultaneous_rate_same_as_standard",
    "warning",
    `Simultaneous Rate ${formatMoney(simultaneousRate)} equals the standard Rate — verify this is intentional or agreed`,
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Date integrity (2026-10-30, Amine)
//
// WHY THESE EXIST
// Dashboard_NEW counts how much work is late, but the ROW never said so: the
// fact lived only in a badge, and the fix lived in a list you had to go find.
// These rules put the deadline fact on the row that owns the deadline, and
// they catch the impossible dates that quietly poison turnaround reporting.
//
// EVERY RULE BELOW IS DELIBERATELY LOW-NOISE. Each was measured against the
// full services table before shipping; two candidates were dropped after that
// measurement — `delivery_date_missing` (936 historical rows: not backfilling
// DeliveryDate is a convention here, not an error) and `invoice_flag_mismatch`
// (5,658 rows: legacy imports set Invoiced without an InvoiceID). A check that
// fires on history is a check people learn to ignore.
// ───────────────────────────────────────────────────────────────────────────

// YYYY-MM-DD from a DATEONLY string or a full timestamp. Blank/unparseable
// → "" so a rule can skip instead of comparing garbage.
const toIsoDay = (value) => {
  if (value === null || value === undefined) return "";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  return match ? `${match[1]}-${match[2]}-${match[3]}` : "";
};

// Whole days from `from` to `to`. UTC-anchored so DST and the local clock
// cannot shift a "1 day late" into "0" or "2".
const diffDays = (from, to) => {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
  return Math.round((end - start) / 86400000);
};

// Business "today" for the date rules. `context.today` wins when the caller
// already resolved it — Dashboard_NEW passes the server's business date so the
// card and the lists can never disagree by a day around midnight. Otherwise the
// browser's local date is used, which is what the person reading the row sees.
const resolveCheckToday = (context) => {
  const override = String(context?.today || "").trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(override)) return override;
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate(),
  ).padStart(2, "0")}`;
};

const isCancelledStatus = (status) =>
  String(status || "").trim().startsWith("Cancel");

// Deadline only means anything for the two categories that owe the client a
// date. Interpretation work is scheduled by `Date`, not by a turnaround.
const isDeadlineCategory = (category) => {
  const token = normalizeServiceCategory(category);
  return token === "Translation" || token === "OtherService";
};

// Open = still owes the client something. Delivered/Completed are done, and a
// cancelled assignment is no longer owed regardless of its status text.
const isOpenWork = (service, category) => {
  const status = getStatus(service, category);
  if (status === "Delivered" || status === "Completed") return false;
  if (isCancelledStatus(status) || isTrue(service?.Cancelled)) return false;
  return true;
};

// deadline_passed — the row-level twin of the sidebar badge and the
// Dashboard_NEW deadline card. A deadline with no date cannot be late, so a
// blank deadline is left to deadline_missing.
const checkDeadlinePassed = (service, context, issues) => {
  if (!isDeadlineCategory(context?.category)) return;
  if (!isOpenWork(service, context?.category)) return;
  const deadline = toIsoDay(service?.Deadline);
  if (!deadline) return;
  const today = resolveCheckToday(context);
  if (deadline >= today) return;
  const status = getStatus(service, context?.category) || "open";
  pushIssue(
    issues,
    "deadline_passed",
    "warning",
    `Deadline passed ${diffDays(deadline, today)} day(s) ago and the assignment is still ${status}`,
  );
};

// deadline_missing — the gap that MANUFACTURES a missed deadline: work with no
// deadline can never appear in any late list. Presence-gated, because a payload
// without the column cannot be judged.
const checkDeadlineMissing = (service, context, issues) => {
  if (!isDeadlineCategory(context?.category)) return;
  if (service?.Deadline === undefined) return;
  if (!isOpenWork(service, context?.category)) return;
  if (toIsoDay(service.Deadline)) return;
  pushIssue(
    issues,
    "deadline_missing",
    "warning",
    "No deadline set — this assignment can never be flagged as late",
  );
};

// deadline_before_date — a deadline that precedes the work itself is always a
// typo, and it silently corrupts every turnaround figure that reads it.
const checkDeadlineBeforeDate = (service, context, issues) => {
  if (!isDeadlineCategory(context?.category)) return;
  const deadline = toIsoDay(service?.Deadline);
  const date = toIsoDay(service?.Date);
  if (!deadline || !date) return;
  if (deadline >= date) return;
  pushIssue(
    issues,
    "deadline_before_date",
    "error",
    `Deadline ${deadline} is before the assignment date ${date}`,
  );
};

// delivery_before_date — delivered before it happened. Same class of typo.
const checkDeliveryBeforeDate = (service, context, issues) => {
  if (!isDeadlineCategory(context?.category)) return;
  const delivery = toIsoDay(service?.DeliveryDate);
  const date = toIsoDay(service?.Date);
  if (!delivery || !date) return;
  if (delivery >= date) return;
  pushIssue(
    issues,
    "delivery_before_date",
    "error",
    `Delivery date ${delivery} is before the assignment date ${date}`,
  );
};

// interpretation_unclosed — the Dashboard_NEW "never closed" card, on the row.
// An interpretation is billed only once Completed, so a row whose date has gone
// by and that is still Requested/Confirmed cannot be invoiced. Work dated today
// is NOT late yet: it may still be in progress.
const checkInterpretationUnclosed = (service, context, issues) => {
  if (normalizeServiceCategory(context?.category) !== "Interpretation") return;
  const status = getStatus(service, "Interpretation");
  if (!status) return;
  if (isCancelledStatus(status) || isTrue(service?.Cancelled)) return;
  if (status !== "Requested" && !status.startsWith("Confirmed")) return;
  const date = toIsoDay(service?.Date);
  if (!date) return;
  const today = resolveCheckToday(context);
  if (date >= today) return;
  pushIssue(
    issues,
    "interpretation_unclosed",
    "warning",
    `Interpretation happened ${diffDays(date, today)} day(s) ago and is still ${status} — it cannot be invoiced until Completed`,
  );
};

// paid_unfinished — money out before the work is in. The cancel gate is on the
// STATUS as well as the flag: notice pay on a cancelled assignment is a real
// business practice (61 such rows exist today), so those must stay silent.
const checkPaidUnfinished = (service, context, issues) => {
  if (!isTrue(service?.LinguistPaid)) return;
  const status = getStatus(service, context?.category);
  if (!status) return;
  if (isCancelledStatus(status) || isTrue(service?.Cancelled)) return;
  if (status === "Delivered" || status === "Completed") return;
  pushIssue(
    issues,
    "paid_unfinished",
    "warning",
    `Linguist is marked paid while the assignment is still ${status}`,
  );
};

// ───────────────────────────────────────────────────────────────────────────
// Cost completeness — Interpretation only (2026-11-03, Amine)
//
// WHY THESE EXIST
// A filled assignment with no money on it cannot pay the linguist or bill the
// client. These three put that gap on the row instead of leaving it to whoever
// notices the blank field. All three are WARNINGS: an unfinished figure is a
// gap to complete, not corrupt data. They never block saving.
//
// ⚠️ MEASURED BEFORE SHIPPING — and the literal predicates were REJECTED.
// The obvious versions (linguist assigned + TotalLinguistPay null/0 = 4,711
// rows, 93% of all live interpretations; not-cancelled + TotalCost null/0 =
// 1,171 rows) fire across the whole history because these legacy money columns
// are NOT stored on most rows — TotalLinguistPay in particular is a cache that
// the server rollup refills from the cost lines (3,105 rows carry payable lines
// with a blank cached total). A rule on the column alone is history noise, the
// same trap that killed delivery_date_missing (936) and invoice_flag_mismatch
// (5,658) in the 2026-10-30 batch.
//
// So every rule below demands COST-LINE EVIDENCE and is presence-gated on
// CostLines: the Interpretations list attaches them for the Checks column, and
// a payload without them cannot be judged, so it skips silently.
//   interpretation_linguist_pay_missing   0 rows measured
//   interpretation_total_cost_missing    24 rows measured (15 non-cancelled)
//   total_cost_without_breakdown          0 rows measured
// ───────────────────────────────────────────────────────────────────────────

// A cost line feeds a total unless the flag is EXPLICITLY false — unset flags
// resolve to the cost type's default (true) on hydration, exactly like the
// flag-driven calculator in InterpretationNewEdit.
const lineFeedsTotal = (line, flagField) => !isFalse(line?.[flagField]);

// Sum of the line amounts that feed Total Cost (positive amounts only — a zero
// line contributes nothing either way).
const sumIncludedTotalCost = (service) =>
  getCostLines(service).reduce((sum, line) => {
    if (!lineFeedsTotal(line, "IncludeInTotalCost")) return sum;
    const amount = toNumber(line?.Amount);
    return amount > 0 ? sum + amount : sum;
  }, 0);

// interpretation_linguist_pay_missing — Completed work with a linguist but no
// pay recorded anywhere: the cached Total Linguist Pay is blank/$0, no stored
// Linguist Pay exists, and no cost line feeds linguist pay. This is the
// "nobody can be paid" state (the 2026-10-08 lines-flagged-false incident was
// exactly this shape, just with a stale total masking it).
const checkInterpretationLinguistPayMissing = (service, context, issues) => {
  if (normalizeServiceCategory(context?.category) !== "Interpretation") return;
  if (!isLinguistSelected(service, context)) return;
  const status = getStatus(service, "Interpretation");
  if (!status.startsWith("Completed")) return;
  if (isCancelledStatus(status) || isTrue(service?.Cancelled)) return;
  if (!Array.isArray(service?.CostLines)) return; // cannot judge without lines
  if (!isZeroOrBlank(service?.TotalLinguistPay)) return;
  if (!isZeroOrBlank(service?.LinguistPay)) return;
  const hasPayableLine = getCostLines(service).some(
    (line) =>
      lineFeedsTotal(line, "IncludeInLinguistPay") &&
      toNumber(line?.Amount) > 0,
  );
  if (hasPayableLine) return;
  pushIssue(
    issues,
    "interpretation_linguist_pay_missing",
    "warning",
    "Completed assignment has a linguist but no pay recorded — Total Linguist Pay is empty and no cost line feeds linguist pay",
  );
};

// interpretation_total_cost_missing — the cost lines carry money that belongs
// in Total Cost, but the stored total is blank/$0. Cancelled rows are exempt:
// their totals are expected to be zero, and any non-zero total is already
// reported by cancelled_nonzero_totals.
const checkInterpretationTotalCostMissing = (service, context, issues) => {
  if (normalizeServiceCategory(context?.category) !== "Interpretation") return;
  const status = getStatus(service, "Interpretation");
  if (isCancelledStatus(status) || isTrue(service?.Cancelled)) return;
  if (!Array.isArray(service?.CostLines)) return; // cannot judge without lines
  if (!isZeroOrBlank(service?.TotalCost)) return;
  const includedTotal = sumIncludedTotalCost(service);
  if (includedTotal <= 0) return;
  pushIssue(
    issues,
    "interpretation_total_cost_missing",
    "warning",
    `Total Cost is empty but the included cost lines add up to ${formatMoney(includedTotal)}`,
  );
};

// total_cost_without_breakdown — the "only a total, nothing specific" case: a
// total exists, no cost line carries any amount, so the figure can never be
// checked, corrected, or explained to the client.
const checkTotalCostWithoutBreakdown = (service, context, issues) => {
  if (normalizeServiceCategory(context?.category) !== "Interpretation") return;
  if (!Array.isArray(service?.CostLines)) return; // cannot judge without lines
  const total = toNumber(service?.TotalCost);
  if (total <= 0) return;
  if (isCancelledStatus(getStatus(service, "Interpretation"))) return;
  if (isTrue(service?.Cancelled)) return;
  if (getCostLines(service).some((line) => toNumber(line?.Amount) > 0)) return;
  pushIssue(
    issues,
    "total_cost_without_breakdown",
    "warning",
    `Total Cost ${formatMoney(total)} has no breakdown — no cost line carries an amount`,
  );
};

// actual_time_order — CONVERTED to DB conditions (2026-10-12): seeded
// conditions own this check now (ActualEnd/ActualStart operators).

// ───────────────────────────────────────────────────────────────────────────
// 2026-11-03 — MONEY INTEGRITY (all categories, all warnings)
//
// Amine: "add more checks for when anything related to $$ is not quite right".
//
// Same discipline as the cost-completeness batch: a rule that fires on history
// is a rule people learn to ignore, so every predicate below is narrow, is
// backed by cost-line evidence where the figure could be stale, and was
// MEASURED against the dev database before shipping:
//
//   money_charge_missing               112 rows / $11,723
//   money_negative_spread             ~110 rows / $100k+ (charge < cost or pay)
//   money_total_cost_blank_with_lines   13 rows (Translation + Other; the 15
//                                       Interpretation rows are already
//                                       reported by interpretation_total_cost_missing)
//   money_paid_without_pay              12 rows
//   money_linguist_pay_drift             0 rows — regression guard for the
//                                       stale-cache bug fixed 2026-11-03
//   money_fee_in_linguist_pay            0 rows — guards the Payment Fees
//                                       decision made 2026-11-03
//
// Deliberately NOT shipped: "stored Total Cost disagrees with its lines"
// (306 rows, but 294 of them are 2023–2024 history where the cache was never
// refreshed). Reported here so the decision is on the record rather than
// silently dropped.
// ───────────────────────────────────────────────────────────────────────────

// A line feeds Total Linguist Pay exactly the way the SERVER calculator counts
// it (helpers/serviceCostCalculator.summarizeServiceCostLines): an explicit
// true flag, or a line that is forced in regardless (base Linguist Pay and
// Mileage). Using the looser "unset means true" rule here would make this
// check disagree with the very column it compares against.
const isForcedLinguistPay = (line) => {
  const legacyKey = normalizeTypeKey(
    line?.SourceLegacyField || line?.CostType?.SourceLegacyField,
  );
  if (legacyKey === "linguistpay") return true;
  return isMileageLine(line);
};

const lineFeedsLinguistPayExactly = (line) =>
  isTrue(line?.IncludeInLinguistPay) || isForcedLinguistPay(line);

// Same strictness for Total Cost: the calculator adds a line only when the
// flag is truthy (no default-on), so the flags are read with isTrue.
const sumIncludedTotalCostStrict = (service) =>
  getCostLines(service).reduce((sum, line) => {
    if (!isTrue(line?.IncludeInTotalCost)) return sum;
    const amount = toNumber(line?.Amount);
    return amount > 0 ? sum + amount : sum;
  }, 0);

const sumIncludedLinguistPayStrict = (service) =>
  getCostLines(service).reduce((sum, line) => {
    if (!lineFeedsLinguistPayExactly(line)) return sum;
    const amount = toNumber(line?.Amount);
    return amount > 0 ? sum + amount : sum;
  }, 0);

// Money is only comparable on live work: a cancelled assignment is expected to
// carry zeros, and its own rule already reports any non-zero total.
const isLiveForMoney = (service, category) => {
  if (isTrue(service?.Cancelled)) return false;
  if (isCancelledStatus(getStatus(service, category))) return false;
  if (isZeroExpectedCancelStatus(getStatus(service, category))) return false;
  return true;
};

// money_charge_missing — we carry cost or owe the linguist, but the client is
// charged nothing. Unbillable work: either the charge was never entered or it
// was zeroed. Requires AccountCharge to be present in the payload.
const checkMoneyChargeMissing = (service, context, issues) => {
  const category = normalizeServiceCategory(context?.category);
  if (service?.AccountCharge === undefined) return; // field not selected
  if (!isLiveForMoney(service, category)) return;
  if (!isZeroOrBlank(service?.AccountCharge)) return;
  const cost = toNumber(service?.TotalCost);
  const pay = toNumber(service?.TotalLinguistPay);
  // Fall back to the lines when the stored totals are blank but the lines are
  // not — that is the #104292 shape and it is still unbillable.
  const linesCost = Array.isArray(service?.CostLines)
    ? sumIncludedTotalCostStrict(service)
    : 0;
  const linesPay = Array.isArray(service?.CostLines)
    ? sumIncludedLinguistPayStrict(service)
    : 0;
  const realCost = cost > 0 ? cost : linesCost;
  const realPay = pay > 0 ? pay : linesPay;
  if (realCost <= 0 && realPay <= 0) return;
  pushIssue(
    issues,
    "money_charge_missing",
    "warning",
    `Client charge is empty but this work costs ${formatMoney(realCost)} and pays the linguist ${formatMoney(realPay)} — it cannot be billed as it stands`,
  );
};

// money_negative_spread — the client is charged LESS than the work costs, or
// less than the linguist is paid. Either is money out of the door on every
// delivery, so it is worth a look even when it is deliberate.
const checkMoneyNegativeSpread = (service, context, issues) => {
  const category = normalizeServiceCategory(context?.category);
  if (service?.AccountCharge === undefined) return;
  if (!isLiveForMoney(service, category)) return;
  const charge = toNumber(service?.AccountCharge);
  if (charge <= 0) return; // reported by money_charge_missing
  const cost = toNumber(service?.TotalCost);
  const pay = toNumber(service?.TotalLinguistPay);
  const problems = [];
  if (cost > 0 && charge < cost - 0.01) {
    problems.push(`cost (${formatMoney(cost)})`);
  }
  if (pay > 0 && charge < pay - 0.01) {
    problems.push(`linguist pay (${formatMoney(pay)})`);
  }
  if (problems.length === 0) return;
  pushIssue(
    issues,
    "money_negative_spread",
    "warning",
    `Client is charged ${formatMoney(charge)}, less than the ${problems.join(" and the ")} — this assignment loses money on every delivery`,
  );
};

// money_total_cost_blank_with_lines — the cost lines carry money that belongs
// in Total Cost but the stored total is blank/$0. Interpretation already has
// interpretation_total_cost_missing, so this rule covers the other two
// categories and the two never double-report.
const checkMoneyTotalCostBlankWithLines = (service, context, issues) => {
  const category = normalizeServiceCategory(context?.category);
  if (category === "Interpretation") return; // covered by the cost-completeness pair
  if (!isLiveForMoney(service, category)) return;
  if (!Array.isArray(service?.CostLines)) return; // cannot judge without lines
  if (!isZeroOrBlank(service?.TotalCost)) return;
  const included = sumIncludedTotalCostStrict(service);
  if (included <= 0) return;
  pushIssue(
    issues,
    "money_total_cost_blank_with_lines",
    "warning",
    `Total Cost is empty but the included cost lines add up to ${formatMoney(included)}`,
  );
};

// money_paid_without_pay — the linguist is marked paid on an assignment that
// has nothing to pay. Either the pay was wiped after the payment was recorded,
// or the "paid" flag was set by mistake.
const checkMoneyPaidWithoutPay = (service, context, issues) => {
  const category = normalizeServiceCategory(context?.category);
  if (service?.LinguistPaid === undefined) return; // field not selected
  if (!isTrue(service?.LinguistPaid)) return;
  if (!isLiveForMoney(service, category)) return;
  if (!isZeroOrBlank(service?.TotalLinguistPay)) return;
  if (!Array.isArray(service?.CostLines)) return; // cannot judge without lines
  // At least one cost line must exist. Without this gate the rule fires on the
  // 2,201 legacy rows that have NO cost lines at all and were marked paid —
  // measured 1,749 hits of pure history, versus 12 real contradictions where a
  // row HAS lines but none of them pays the linguist. "No lines" is a
  // missing-data problem reported elsewhere; this rule is about a paid flag
  // that contradicts a line set the portal can actually see.
  if (getCostLines(service).length === 0) return;
  if (sumIncludedLinguistPayStrict(service) > 0) return;
  pushIssue(
    issues,
    "money_paid_without_pay",
    "warning",
    "Linguist is marked as paid but none of this assignment's cost lines pay the linguist — nothing was actually owed",
  );
};

// money_linguist_pay_drift — the stored Total Linguist Pay disagrees with the
// pay lines it is supposed to be the sum of. Regression guard: this is exactly
// the bug class found on 2026-11-03 (a stale cache hid $2,204.89 of pay and
// made rows vanish from Linguist Payments).
const checkMoneyLinguistPayDrift = (service, context, issues) => {
  const category = normalizeServiceCategory(context?.category);
  if (!isLiveForMoney(service, category)) return;
  if (!Array.isArray(service?.CostLines)) return; // cannot judge without lines
  const stored = toNumber(service?.TotalLinguistPay);
  const computed = sumIncludedLinguistPayStrict(service);
  if (stored <= 0 || computed <= 0) return;
  const drift = stored - computed;
  if (Math.abs(drift) <= 0.01) return;
  pushIssue(
    issues,
    "money_linguist_pay_drift",
    "warning",
    `Total Linguist Pay is stored as ${formatMoney(stored)} but the pay lines add up to ${formatMoney(computed)} — re-save to refresh`,
  );
};

// money_fee_in_linguist_pay — a Payment Fees line is counting toward pay owed
// to the linguist. Amine's 2026-11-03 decision: fees are a cost of getting
// paid, not pay. Guards against the flag creeping back in (it was set in the
// cost type, in all three category rules, and in the new-assignment defaults).
const checkMoneyFeeInLinguistPay = (service, context, issues) => {
  const category = normalizeServiceCategory(context?.category);
  if (!isLiveForMoney(service, category)) return;
  for (const line of getCostLines(service)) {
    if (normalizeTypeKey(line?.SourceLegacyField) !== "paymentfees") continue;
    if (!isTrue(line?.IncludeInLinguistPay)) continue;
    const amount = toNumber(line?.Amount);
    if (amount <= 0) continue;
    pushIssue(
      issues,
      "money_fee_in_linguist_pay",
      "warning",
      `Payment Fees ${formatMoney(amount)} is counting as linguist pay — fees belong to Total Cost only`,
    );
    return;
  }
};


// 2026-10-09 — Preparation + Scheduling are paid time carved out of the
// booked Duration; together they must leave core interpreting time.
const checkPrepSchedExceedDuration = (service, context, issues) => {
  const durationMinutes = parseDurationToMinutes(service?.Duration);
  if (durationMinutes === null || durationMinutes <= 0) return;
  const prepMinutes = parseDurationToMinutes(service?.PreparationTime) ?? 0;
  const schedMinutes = parseDurationToMinutes(service?.SchedulingTime) ?? 0;
  if (prepMinutes + schedMinutes >= durationMinutes - 0.01) {
    pushIssue(
      issues,
      "prep_sched_exceed_duration",
      "warning",
      `Preparation (${formatDurationMinutes(prepMinutes)}) + Scheduling (${formatDurationMinutes(schedMinutes)}) leave no core time within Duration (${String(service?.Duration).trim()})`,
    );
  }
};

// ---------------------------------------------------------------------------
// Settings-managed check configuration (GET /validationChecks)
// ---------------------------------------------------------------------------

// Module-level cache — the config only changes from Settings, so one
// successful fetch serves every screen until invalidated. `null` cache =
// not loaded (or the last load failed) → callers fall back to defaults.
let validationCheckConfigCache = null;
let validationCheckConfigRequest = null;

// Coerce a config row's Params (object, or JSON string from the DB) into an
// object — anything unusable resolves to null and the rule uses its default.
const parseCheckParams = (params) => {
  if (params === null || params === undefined || params === "") return null;
  if (typeof params === "string") {
    try {
      const parsed = JSON.parse(params);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch {
      return null;
    }
  }
  return typeof params === "object" ? params : null;
};

// Only the engine's two severities are honored; anything else (blank, new
// value) keeps the rule's built-in severity.
const normalizeCheckSeverity = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return normalized === "error" || normalized === "warning"
    ? normalized
    : null;
};

// ---------------------------------------------------------------------------
// Custom-check row normalization (Settings CONDITIONS builder payloads)
// ---------------------------------------------------------------------------

// 'All' | 'Any' → canonical casing; anything else → 'All'.
const normalizeCheckLogic = (value) =>
  String(value || "").trim().toLowerCase() === "any" ? "Any" : "All";

// 'Applies' | 'Fails' → canonical casing; anything else → 'Applies'.
const normalizeCheckClause = (value) =>
  String(value || "").trim().toLowerCase() === "fails" ? "Fails" : "Applies";

// Per-row join for the left-to-right clause fold — 'OR'/'AND' canonical; a
// missing/invalid value stays null so the clause logic (AppliesLogic/
// FailsLogic) can drive that row's join instead.
const normalizeJoinOperator = (value) => {
  const normalized = String(value ?? "").trim().toUpperCase();
  if (normalized === "OR") return "OR";
  if (normalized === "AND") return "AND";
  return null;
};

// Conditions travel as an array (JSON column) or a JSON string (TEXT
// column) — never throws, unusable payloads resolve to [].
const parseConditionsPayload = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim() !== "") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const normalizeCheckCondition = (condition, index) => ({
  Clause: normalizeCheckClause(condition?.Clause),
  Seq: Number.isFinite(Number(condition?.Seq))
    ? Number(condition.Seq)
    : index + 1,
  JoinOperator: normalizeJoinOperator(condition?.JoinOperator),
  ColumnName: String(condition?.ColumnName ?? "").trim(),
  // Operator is kept VERBATIM — an unknown operator evaluates to false in
  // the engine and must not be silently rewritten to something else.
  Operator: String(condition?.Operator ?? "").trim(),
  Value:
    condition?.Value === undefined || condition?.Value === null
      ? null
      : condition.Value,
  CompareColumn: hasValue(condition?.CompareColumn)
    ? String(condition.CompareColumn).trim()
    : null,
  Tolerance: (() => {
    const tolerance = toNullableNumber(condition?.Tolerance);
    return tolerance === null || tolerance < 0 ? null : tolerance;
  })(),
});

// One clean shape for every consumer: IsBuiltIn 1/0, canonical
// AppliesLogic/FailsLogic and a normalized Conditions array.
const normalizeCheckConfigRow = (row) => {
  if (!row || typeof row !== "object") return row;
  return {
    ...row,
    IsBuiltIn: isTrue(row.IsBuiltIn) ? 1 : 0,
    AppliesLogic: normalizeCheckLogic(row.AppliesLogic),
    FailsLogic: normalizeCheckLogic(row.FailsLogic),
    Conditions: parseConditionsPayload(row.Conditions).map(
      normalizeCheckCondition,
    ),
  };
};

// Resolves to the config ARRAY, or null when unavailable (fetch failed or a
// non-array payload) — callers then run with pure engine defaults. Concurrent
// calls share one in-flight request; a success is cached until invalidated.
export const loadValidationCheckConfig = async () => {
  if (validationCheckConfigCache) return validationCheckConfigCache;
  if (validationCheckConfigRequest) return validationCheckConfigRequest;
  const request = (async () => {
    try {
      const response = await api.get("/validationChecks");
      const payload = Array.isArray(response?.data) ? response.data : null;
      // Normalize the create/edit fields (IsBuiltIn, AppliesLogic, FailsLogic,
      // Conditions with Tolerance/JoinOperator) once per load so consumers
      // and the custom-check evaluator see one clean shape.
      const rows = payload ? payload.map(normalizeCheckConfigRow) : null;
      if (rows) validationCheckConfigCache = rows;
      return rows;
    } catch {
      return null;
    } finally {
      if (validationCheckConfigRequest === request) {
        validationCheckConfigRequest = null;
      }
    }
  })();
  validationCheckConfigRequest = request;
  return request;
};

// Settings calls this after a save so the next load picks up the new rows.
export const invalidateValidationCheckConfig = () => {
  validationCheckConfigCache = null;
  validationCheckConfigRequest = null;
};

// code (lowercased) → config row. Built once per run (27 rows max) so rules
// stay untouched and the overlay applies a single time in the entry point.
const buildCheckConfigMap = (config) => {
  if (!Array.isArray(config) || config.length === 0) return null;
  const map = {};
  for (const row of config) {
    const code = String(row?.Code || "")
      .trim()
      .toLowerCase();
    if (code) map[code] = row;
  }
  return Object.keys(map).length > 0 ? map : null;
};

// Params for one code from the resolved map (object or JSON string).
const getResolvedCheckParams = (context, code) =>
  parseCheckParams(context?.__resolvedCheckConfig?.[code]?.Params);

// ±tolerance for simultaneous_rate_same_as_standard — Settings can override
// via Params.tolerance; anything unusable falls back to ±$0.005.
const getSimultaneousRateTolerance = (context) => {
  const params = getResolvedCheckParams(
    context,
    "simultaneous_rate_same_as_standard",
  );
  const tolerance = hasValue(params?.tolerance)
    ? Number(params.tolerance)
    : NaN;
  return Number.isFinite(tolerance) && tolerance >= 0 ? tolerance : 0.005;
};

// Settings overlay: drop codes disabled in Settings, apply configured
// severities. Engine messages stay untouched; unknown codes and missing
// config rows keep default behavior (enabled, engine severity).
const applyValidationCheckConfigOverlay = (issues, configMap) => {
  if (!configMap) return issues;
  const overlaid = [];
  for (const issue of issues) {
    const row = configMap[String(issue?.code || "").trim().toLowerCase()];
    if (!row) {
      overlaid.push(issue);
      continue;
    }
    if (isFalse(row?.IsEnabled)) continue;
    const severity = normalizeCheckSeverity(row?.Severity);
    overlaid.push(
      severity && severity !== issue.severity ? { ...issue, severity } : issue,
    );
  }
  return overlaid;
};

// ---------------------------------------------------------------------------
// Converted built-ins (condition-backed, 2026-10-12)
// ---------------------------------------------------------------------------

// Built-ins that are NO LONGER evaluated by code rule fns in this file —
// their behavior ships as seeded DB Conditions evaluated with the custom-
// check machinery (Applies gate + Fails fire) by runServiceChecks. The
// Settings editor guard-calls isConditionBackedBuiltIn to render these rows
// with the CONDITIONS builder instead of the code-backed explanation.
export const CONDITION_BACKED_BUILTIN_CODES = [
  "simultaneous_rate_missing",
  "simultaneous_client_rate_missing",
  "charge_zero_completed",
  "completed_missing_actuals",
  "actual_time_order",
  "paid_no_charge",
  "cancelled_nonzero_totals",
  "time_order",
];

// String code → boolean. Case-insensitive; unknown/blank codes are never
// condition-backed (they keep whatever behavior their producer gives them).
export const isConditionBackedBuiltIn = (code) =>
  CONDITION_BACKED_BUILTIN_CODES.includes(
    String(code ?? "")
      .trim()
      .toLowerCase(),
  );

// ---------------------------------------------------------------------------
// Custom checks (Settings CONDITIONS builder) — category-aware evaluator
// ---------------------------------------------------------------------------

// WeakMap keyed by the config ARRAY identity → normalized custom rows. The
// config object is stable between invalidations, so normalization (and the
// per-field lookups below) only run once per loaded config, never per row.
const customServiceCheckCache = new WeakMap();

// Per-row normalization cache: the array-identity cache above serves the
// selection paths, this one serves consumers that see rows one at a time
// (per-issue scope filtering) so a row normalizes at most once per identity.
const normalizedConfigRowCache = new WeakMap();
const getNormalizedConfigRow = (row) => {
  if (!row || typeof row !== "object") return row;
  const cached = normalizedConfigRowCache.get(row);
  if (cached) return cached;
  const normalized = normalizeCheckConfigRow(row);
  normalizedConfigRowCache.set(row, normalized);
  return normalized;
};

const getNormalizedCustomChecks = (config) => {
  if (!Array.isArray(config) || config.length === 0) return [];
  const cached = customServiceCheckCache.get(config);
  if (cached) return cached;

  const normalized = [];
  for (const row of config) {
    if (!row || typeof row !== "object" || isTrue(row.IsBuiltIn)) continue;
    if (!String(row.Code || "").trim()) continue; // code keys the issue pipeline
    normalized.push(getNormalizedConfigRow(row));
  }
  customServiceCheckCache.set(config, normalized);
  return normalized;
};

// Converted (condition-backed) built-in rows — IsBuiltIn set AND the code
// in the converted list. A SEPARATE selection from the customs path so a
// converted row evaluates exactly once (built-ins stay excluded from
// getNormalizedCustomChecks).
const convertedBuiltInCache = new WeakMap();
const getNormalizedConvertedBuiltIns = (config) => {
  if (!Array.isArray(config) || config.length === 0) return [];
  const cached = convertedBuiltInCache.get(config);
  if (cached) return cached;

  const normalized = [];
  for (const row of config) {
    if (!row || typeof row !== "object" || !isTrue(row.IsBuiltIn)) continue;
    if (!isConditionBackedBuiltIn(row.Code)) continue;
    normalized.push(getNormalizedConfigRow(row));
  }
  convertedBuiltInCache.set(config, normalized);
  return normalized;
};

// Custom checks whose ServiceCategory scope covers the list being evaluated
// — CSV-aware via checkAppliesToCategory, so legacy "All" rows and the
// Other↔OtherService literal both match. Blank/unknown scopes match no
// category (the pre-CSV strict-match result).
const getCustomChecksForCategory = (config, category) =>
  getNormalizedCustomChecks(config).filter((row) =>
    checkAppliesToCategory(row, category),
  );

// Converted built-ins whose scope covers the list being evaluated.
const getConvertedBuiltInsForCategory = (config, category) =>
  getNormalizedConvertedBuiltIns(config).filter((row) =>
    checkAppliesToCategory(row, category),
  );

const getClauseConditions = (check, clause) =>
  check.Conditions.filter((condition) => condition.Clause === clause).sort(
    (a, b) => a.Seq - b.Seq,
  );

// Value is a JSON array string (parse failure → single-item list so a
// hand-typed value still compares as one entry).
const parseListValueForCheck = (value) => {
  if (Array.isArray(value)) return value;
  if (value === null || value === undefined) return [];
  const text = String(value).trim();
  if (text === "") return [];
  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed : [parsed];
  } catch {
    return [text];
  }
};

// Tolerance from the condition; blank/invalid/negative falls back.
const getToleranceForCondition = (condition, fallback) => {
  const tolerance = toNullableNumber(condition?.Tolerance);
  return tolerance === null || tolerance < 0 ? fallback : tolerance;
};

// equals/notEquals core: numeric when both sides are numbers, boolean when
// both sides are boolean-ish, else trimmed case-insensitive string.
const evaluateEquality = (leftValue, rightValue, tolerance) => {
  const leftNumber = toNullableNumber(leftValue);
  const rightNumber = toNullableNumber(rightValue);
  if (leftNumber !== null && rightNumber !== null) {
    return Math.abs(leftNumber - rightNumber) <= tolerance;
  }
  const leftBoolean = asBooleanish(leftValue);
  const rightBoolean = asBooleanish(rightValue);
  if (leftBoolean !== null && rightBoolean !== null) {
    return leftBoolean === rightBoolean;
  }
  return normalizeString(leftValue) === normalizeString(rightValue);
};

// greaterThan/greaterThanOrEqual/lessThan/lessThanOrEqual and their
// *Field twins — blank or non-numeric on either side → false.
//
// Time exception (2026-10-12): lessThanField/greaterThanField compare TWO
// time-of-day strings ("H:mm[:ss]") as minutes since midnight through the
// shared parseTimeToMinutes helper. Without this, the numeric coercion
// below nulls time strings, so the reversed-times half of the seeded
// time_order/actual_time_order built-ins could never fire. One-sided
// times (or values that are not times at all) keep the numeric behavior.
const evaluateNumericComparison = (operator, leftValue, rightValue) => {
  if (operator === "lessThanField" || operator === "greaterThanField") {
    const leftMinutes = parseTimeToMinutes(leftValue);
    const rightMinutes = parseTimeToMinutes(rightValue);
    if (leftMinutes !== null && rightMinutes !== null) {
      return operator === "lessThanField"
        ? leftMinutes < rightMinutes
        : leftMinutes > rightMinutes;
    }
  }
  const left = toNullableNumber(leftValue);
  const right = toNullableNumber(rightValue);
  if (left === null || right === null) return false;
  const base = operator.endsWith("Field")
    ? operator.slice(0, -"Field".length)
    : operator;
  if (base === "greaterThan") return left > right;
  if (base === "greaterThanOrEqual") return left >= right;
  if (base === "lessThan") return left < right;
  if (base === "lessThanOrEqual") return left <= right;
  return false;
};

const evaluateTextCondition = (operator, leftValue, rightValue) => {
  const left = normalizeString(leftValue);
  const right = normalizeString(rightValue);
  if (operator === "contains") return left.includes(right);
  if (operator === "notContains") return !left.includes(right);
  if (operator === "startsWith") return left.startsWith(right);
  if (operator === "endsWith") return left.endsWith(right);
  return false;
};

const evaluateMembership = (operator, leftValue, rawValue) => {
  const left = normalizeString(leftValue);
  const found = parseListValueForCheck(rawValue).some(
    (item) => normalizeString(item) === left,
  );
  return operator === "in" ? found : !found;
};

const evaluateBetween = (leftValue, rawValue) => {
  const left = toNullableNumber(leftValue);
  const list = parseListValueForCheck(rawValue);
  const min = toNullableNumber(list[0]);
  const max = toNullableNumber(list[1]);
  if (left === null || min === null || max === null) return false;
  return left >= min && left <= max;
};

const FIELD_OPERATORS = [
  "equalsField",
  "notEqualsField",
  "greaterThanField",
  "lessThanField",
  "equalsFieldTolerance",
];

// Condition-column read: the VIRTUAL "Status" column resolves through the
// CATEGORY's status field (Interpretation/Translation/OtherService) so one
// seeded condition row works across every scope. The VIRTUAL
// "InterpretationType" column mirrors the server catalog contract
// (ServiceSubtype.Name — Interpretation only) and resolves through
// getInterpretationTypeName so both row shapes work: embedded payloads read
// ServiceSubtype.Name, flat rows read InterpretationType. This also repairs
// custom checks built on this column. Everything else reads the stored
// field directly.
const getConditionColumnValue = (service, columnName, evalContext) =>
  columnName === "Status"
    ? getStatus(service, evalContext?.category)
    : columnName === "InterpretationType"
      ? getInterpretationTypeName(service)
      : service?.[columnName];

// One condition against one service row. Unknown operators are false.
const evaluateCondition = (condition, service, evalContext) => {
  const operator = condition.Operator;
  const left = getConditionColumnValue(
    service,
    condition.ColumnName,
    evalContext,
  );
  const right = FIELD_OPERATORS.includes(operator)
    ? getConditionColumnValue(service, condition.CompareColumn, evalContext)
    : condition.Value;

  switch (operator) {
    case "equals":
    case "equalsField":
      return evaluateEquality(left, right, getToleranceForCondition(condition, 0));
    case "notEquals":
    case "notEqualsField":
      return !evaluateEquality(left, right, getToleranceForCondition(condition, 0));
    case "equalsFieldTolerance": {
      const leftNumber = toNullableNumber(left);
      const rightNumber = toNullableNumber(right);
      if (leftNumber === null || rightNumber === null) return false;
      return (
        Math.abs(leftNumber - rightNumber) <=
        getToleranceForCondition(condition, 0.005)
      );
    }
    case "greaterThan":
    case "greaterThanOrEqual":
    case "lessThan":
    case "lessThanOrEqual":
    case "greaterThanField":
    case "lessThanField":
      return evaluateNumericComparison(operator, left, right);
    case "between":
      return evaluateBetween(left, condition.Value);
    case "contains":
    case "notContains":
    case "startsWith":
    case "endsWith":
      return evaluateTextCondition(operator, left, right);
    case "in":
    case "notIn":
      return evaluateMembership(operator, left, condition.Value);
    case "isBlank":
      return isBlankValue(left);
    case "isNotBlank":
      return !isBlankValue(left);
    case "isZero":
      return Number(left || 0) === 0;
    case "isNotZero":
      return Number(left || 0) !== 0;
    default:
      return false;
  }
};

// Left-to-right fold of one clause: each row joins the accumulated result
// via its JoinOperator; a row without one uses the clause logic. No rows →
// null ("no opinion" — the caller decides the default). evalContext carries
// the category so the virtual "Status" column resolves correctly.
const evaluateConditionClause = (conditions, logic, service, evalContext) => {
  const defaultJoin =
    String(logic || "").trim().toLowerCase() === "any" ? "OR" : "AND";
  let result = null;
  for (const condition of conditions) {
    const passed = evaluateCondition(condition, service, evalContext);
    if (result === null) {
      result = passed;
      continue;
    }
    const join =
      condition.JoinOperator === "OR" || condition.JoinOperator === "AND"
        ? condition.JoinOperator
        : defaultJoin;
    result = join === "OR" ? result || passed : result && passed;
  }
  return result;
};

// Fails → the { code, severity, message } issue for this row; null when the
// check does not fire. Applies: no conditions → every row; Fails: no
// conditions → never fires (the editor enforces at least one). evalContext
// carries the category so the virtual "Status" column resolves correctly.
const evaluateCustomCheck = (check, service, evalContext) => {
  const appliesConditions = getClauseConditions(check, "Applies");
  const applies =
    appliesConditions.length === 0 ||
    evaluateConditionClause(
      appliesConditions,
      check.AppliesLogic,
      service,
      evalContext,
    ) === true;
  if (!applies) return null;

  const failsConditions = getClauseConditions(check, "Fails");
  if (failsConditions.length === 0) return null;
  if (
    evaluateConditionClause(
      failsConditions,
      check.FailsLogic,
      service,
      evalContext,
    ) !== true
  ) {
    return null;
  }

  const code = String(check.Code || "").trim();
  if (!code) return null;
  return {
    code,
    severity: normalizeCheckSeverity(check.Severity) || "warning",
    message:
      String(check.Description || "").trim() ||
      String(check.Name || "").trim() ||
      code,
  };
};

// Public entry for the custom checks of one category: returns issues for
// one service row, category-scoped (CSV-aware) and with the virtual
// "Status" column resolving through that category. Kept for back-compat —
// new consumers should call runServiceChecks so the CODE-BACKED and
// converted built-ins run too (severity comes from the check's own row).
export const runCustomServiceChecks = (
  service,
  category,
  checkConfig = null,
) => {
  const issues = [];
  if (!service || typeof service !== "object") return issues;
  const evalContext = {
    category: normalizeServiceCategory(category) || "Interpretation",
  };
  for (const check of getCustomChecksForCategory(checkConfig, category)) {
    try {
      const issue = evaluateCustomCheck(check, service, evalContext);
      if (issue) issues.push(issue);
    } catch {
      // A broken custom check must never break the host screen.
    }
  }
  return issues;
};

// ---------------------------------------------------------------------------
// Engine
// ---------------------------------------------------------------------------

// Code-backed rule fns only. The 8 converted (condition-backed) built-ins
// are intentionally absent — runServiceChecks evaluates those from the
// seeded config Conditions. Every identifier below is defined in this file.
const CHECKS = [
  checkMissingLinguist,
  checkAutoTotals,
  checkMileage,
  checkMileageFlagExcluded,
  checkLineExcludedAllTotals,
  checkDurationMismatch,
  checkProfitMargin,
  checkCancelledFlagMismatch,
  checkContactsIncomplete,
  checkEmailThreadInvalid,
  checkInPersonNoMileage,
  checkVirtualPhoneMileage,
  checkSimultaneousRateSameAsStandard,
  checkPrepSchedExceedDuration,
  // 2026-10-30 — date integrity (see the block above the rules)
  checkDeadlinePassed,
  checkDeadlineMissing,
  checkDeadlineBeforeDate,
  checkDeliveryBeforeDate,
  checkInterpretationUnclosed,
  checkPaidUnfinished,
  // 2026-11-03 — cost completeness (Interpretation only, all warnings)
  checkInterpretationLinguistPayMissing,
  checkInterpretationTotalCostMissing,
  checkTotalCostWithoutBreakdown,
  // 2026-11-03 — money integrity (all categories, all warnings)
  checkMoneyChargeMissing,
  checkMoneyNegativeSpread,
  checkMoneyTotalCostBlankWithLines,
  checkMoneyPaidWithoutPay,
  checkMoneyLinguistPayDrift,
  checkMoneyFeeInLinguistPay,
];

// Scope gate for a CODE-BACKED built-in's issues (2026-10-12): the rule fns
// push { code, ... } issues without knowing the category, so the row-level
// scope and the Settings-added Applies gate are applied per issue HERE.
// Rows missing from the config keep legacy behavior (in scope).
const isCodeIssueInScope = (issue, configMap, service, evalContext) => {
  if (!configMap) return true;
  const row = configMap[String(issue?.code || "").trim().toLowerCase()];
  if (!row) return true;
  if (!checkAppliesToCategory(row, evalContext.category)) return false;
  // User-added Applies-clause Conditions act as an ADDITIONAL GATE on
  // code-backed rows; Fails clauses are ignored (the Settings UI hides
  // them). No Applies rows → no gate (legacy behavior preserved).
  const normalizedRow = getNormalizedConfigRow(row);
  const appliesConditions = getClauseConditions(normalizedRow, "Applies");
  if (appliesConditions.length === 0) return true;
  return (
    evaluateConditionClause(
      appliesConditions,
      normalizedRow.AppliesLogic,
      service,
      evalContext,
    ) === true
  );
};

// Unified category-aware runner (2026-10-12). `category` is
// "Interpretation" | "Translation" | "OtherService" — the services row
// literal "Other" and legacy "All" scopes are tolerated; unknown/blank
// categories fall back to "Interpretation". Returns ONE merged issue list
// (identical { code, severity, message } shape throughout) with the
// Settings overlay (enable/disable + severity) applied once at the end:
//   1. CODE-BACKED built-ins — the surviving rule fns, then per-issue
//      scope filtering against the config row (CSV category + Applies gate).
//   2. CONVERTED built-ins — seeded DB Conditions evaluated with the
//      custom-check machinery (Applies gate + Fails fire), scoped by the
//      row's CSV ServiceCategory, disabled rows skipped.
//   3. CUSTOM checks — Settings-defined rows scoped to this category.
// The resolved category is threaded to the rules and clause evaluators so
// status reads and the virtual "Status" column use the right table column.
export const runServiceChecks = (service, category, context = {}) => {
  const issues = [];
  if (!service || typeof service !== "object") return issues;

  const resolvedCategory =
    normalizeServiceCategory(category) || "Interpretation";

  // Resolve the Settings config once per run; the map powers Params
  // overrides and the scope/Applies gates. No config → rules and defaults
  // run untouched.
  const { checkConfig, ...restContext } = context || {};
  const configMap = buildCheckConfigMap(checkConfig);
  const resolvedContext = {
    ...restContext,
    category: resolvedCategory,
    ...(configMap ? { __resolvedCheckConfig: configMap } : {}),
  };
  const evalContext = { category: resolvedCategory };

  for (const check of CHECKS) {
    try {
      const codeIssues = [];
      check(service, resolvedContext, codeIssues);
      for (const issue of codeIssues) {
        if (isCodeIssueInScope(issue, configMap, service, evalContext)) {
          issues.push(issue);
        }
      }
    } catch {
      // Automatic checks must never break the host screen — a rule that
      // cannot run (unexpected shape) skips silently.
    }
  }

  // Converted built-ins: the seeded conditions ARE the check.
  for (const row of getConvertedBuiltInsForCategory(
    checkConfig,
    resolvedCategory,
  )) {
    if (isFalse(row.IsEnabled)) continue;
    try {
      const issue = evaluateCustomCheck(row, service, evalContext);
      if (issue) issues.push(issue);
    } catch {
      // A broken converted check must never break the host screen.
    }
  }

  // Settings-defined custom checks for this category.
  for (const issue of runCustomServiceChecks(
    service,
    resolvedCategory,
    checkConfig,
  )) {
    issues.push(issue);
  }

  // The overlay drops disabled codes and applies configured severities to
  // built-in, converted and custom issues through the same pipeline.
  return applyValidationCheckConfigOverlay(issues, configMap);
};

// Back-compat entry (all Interpretation screens import this): identical
// results to runServiceChecks(service, "Interpretation", context).
export const runInterpretationChecks = (service, context = {}) =>
  runServiceChecks(service, "Interpretation", context);

export const getWorstCheckSeverity = (issues) => {
  if (!Array.isArray(issues) || issues.length === 0) return null;
  let hasWarning = false;
  for (const issue of issues) {
    if (issue?.severity === "error") return "error";
    if (issue?.severity === "warning") hasWarning = true;
  }
  return hasWarning ? "warning" : null;
};
